'use client'

import DataWithLabel from '@lyra/core/components/DataWithLabel'
import Section, { SectionProps } from '@lyra/core/components/Section'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatTruncatedUSD from '@lyra/core/utils/formatTruncatedUSD'
import { InstrumentType } from '@lyra/web/constants/instruments'
import { CurrencyId } from '@lyra/web/constants/tokens'
import useAllMarketStats from '@lyra/web/hooks/useAllMarketStats'
import useCurrencies from '@lyra/web/hooks/useCurrencies'
import { getInstrumentPagePath } from '@lyra/web/utils/instruments'
import { useRouter } from 'next/navigation'
import React, { useMemo } from 'react'

import MarketsTable from '../trade/MarketsTable'

type Props = SectionProps

export default function HomeMarketsSection({ ...sectionProps }: Props) {
  const { data: marketStats, isLoading: isLoadingMarketStats } = useAllMarketStats()
  const currencies = useCurrencies()

  const marketStatsList = useMemo(
    () =>
      marketStats
        ? filterNulls(
            Object.values(InstrumentType).flatMap((type) => Object.values(marketStats[type]))
          )
        : [],
    [marketStats]
  )

  const totalVolume24h = useMemo(() => {
    return marketStatsList
      ? marketStatsList.reduce((sum, stat) => sum + +stat.daily_notional_volume, 0)
      : 0
  }, [marketStatsList])

  const totalTrades24h = useMemo(() => {
    return marketStatsList.reduce((sum, stat) => sum + stat.daily_trades, 0)
  }, [marketStatsList])

  const totalOpenInterestDollars = useMemo(() => {
    return marketStatsList.reduce((sum, stat) => {
      const spotPrice = currencies[stat.currency as CurrencyId]?.spotPrice ?? 0
      const openInterest = +stat.open_interest
      return sum + openInterest * spotPrice
    }, 0)
  }, [marketStatsList, currencies])

  const router = useRouter()

  return (
    <Section {...sectionProps}>
      <Section.Header title="Markets" />
      <Section.Grid minColWidth={120} maxColWidth={130}>
        <DataWithLabel
          label="24h Volume"
          value={isLoadingMarketStats ? '...' : formatTruncatedUSD(totalVolume24h)}
        />
        <DataWithLabel
          label="24h Trades"
          value={isLoadingMarketStats ? '...' : formatNumber(totalTrades24h)}
        />
        <DataWithLabel
          label="Open Interest"
          value={isLoadingMarketStats ? '...' : formatTruncatedUSD(totalOpenInterestDollars)}
        />
      </Section.Grid>
      <Section.Table>
        <MarketsTable
          onPressMarket={(currencyId, instrumentType) =>
            router.push(getInstrumentPagePath({ marketId: currencyId, type: instrumentType }))
          }
        />
      </Section.Table>
    </Section>
  )
}
